/* @flow */
import React from 'react';
import { Container } from 'flux/utils';

import getModalToDisplayForQuery from 'lib/getModalToDisplayForQuery';

import AffiliationStore from 'stores/AffiliationStore';
import CurrentUser from 'auth/CurrentUser';
import Group, { DEFAULT_COLOR } from 'entities/Group';
import Organization from 'entities/Organization';
import OrganizationActions from 'actions/OrganizationActions';
import OrganizationStore from 'stores/OrganizationStore';
import { needsOptIn, needsEmailConf } from 'shapes/Affiliation';
import GroupView from 'groups/components/GroupView.jsx';
import GroupStore from 'groups/stores/GroupStore';

import { mountModal } from 'lib/modal';

class GroupContainer extends React.Component {
  /*
   Handles redirecting from /classes/:groupCode to:
     classes?join=groupCode
     classes/:groupCode/messages
     classes/:groupCode/people
     classes/:groupCode/verify

   And from:
     classes/:groupCode/settings to /classes/:groupCode/messages/?settings=true
     classes/:groupCode/invite   to /classes/:groupCode/messages/?invite=true
   */
  static onEnter(nextState, replaceWith) {
    const location = nextState.location.pathname.replace(/\/$/, '');
    const { query } = nextState.location;
    const { groupCode } = nextState.params;
    const group = GroupStore.getGroupForCode(groupCode);

    if (groupCode === 'new') {
      replaceWith('/classes?action=new-class');
      return;
    }

    /* If the group doesn't exist show them to the join group modal */
    if (!group) {
      if (location.endsWith(`/${groupCode}`)) {
        replaceWith(`/classes?join=${groupCode}`);
      } else {
        replaceWith('/classes');
      }
      return;
    }

    const ownedByMe = group.isOwnedByUser(CurrentUser.getUserId());

    let doRedirect = false;
    if (location.endsWith(`/${groupCode}/settings`)) {
      query.settings = true;
      doRedirect = true;
    } else if (ownedByMe && location.endsWith(`/${groupCode}/invite`)) {
      query.invite = true;
      doRedirect = true;
    } else if (location.endsWith(`/${groupCode}`)) {
      doRedirect = true;
    }

    const preventVerify = (
      CurrentUser.getUser().hasFeature('web_admin_console') && (
        group.getType() === 'district' ||
        group.isStaffClass()
      )
    );

    if (doRedirect) {
      const org = group.getOrganization();
      if (!group.isRoot() && ownedByMe && group.getMembershipsCount() < 3) {
        replaceWith({ pathname: `/classes/${groupCode}/people`, query });
      } else if (org) {
        const affiliation = AffiliationStore.getAffiliation(org.id);
        if (!preventVerify && affiliation && (needsOptIn(affiliation) || needsEmailConf(affiliation))) {
          replaceWith({ pathname: `/classes/${groupCode}/verify`, query });
        } else {
          replaceWith({ pathname: `/classes/${groupCode}/messages`, query });
        }
      } else {
        replaceWith({ pathname: `/classes/${groupCode}/messages`, query });
      }
    }
  }

  static propTypes = {
    children: React.PropTypes.node.isRequired,
    location: React.PropTypes.object.isRequired,
    params: React.PropTypes.object.isRequired,
  };

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static childContextTypes = {
    group: React.PropTypes.instanceOf(Group).isRequired,
  };

  static getStores() {
    return [
      AffiliationStore,
      GroupStore,
      OrganizationStore,
    ];
  }

  static calculateState(prevState, props) {
    const { groupCode } = props.params;
    const group = GroupStore.getGroupForCode(groupCode);

    // Warning!
    // The group identified by groupCode may have been removed from GroupStore,
    // because the user has unsubscribed from the group.
    if (!group) {
      // Do not update with a null group!
      return prevState;
    }

    let userIsAdmin = false;
    let org = null;
    if (group && group.isStaffClass()) {
      const orgId = group.getStaffClassOrgId();
      org = OrganizationStore.getOrganization(orgId);
      userIsAdmin = !!org && org.isAdminBy(CurrentUser.getUser());
    }

    const accentColor = group.getAccentColor();

    return {
      accentColor,
      userIsAdmin,
      group,
      joinCode: group ? null : groupCode,
      staffClassOrganization: org,
    };
  }

  state: {
    modal?: ?React$Element<*>,
    accentColor: string,
    group: Group;
    joinCode: string;
    staffClassOrganization: Organization;
    userIsAdmin: bool;
  };

  getChildContext() {
    return {
      group: this.state.group,
    };
  }

  componentWillMount() {
    const { groupCode } = this.props.params;
    const group = GroupStore.getGroupForCode(groupCode);

    if (group) {
      this.checkLoadOrg({});
      this.showModal(this.props.location, group);
    }
  }

  componentWillUpdate(nextProps, nextState) {
    this.setState({ modal: null });
    this.showModal(nextProps.location, nextState.group);
  }

  componentDidUpdate(prevProps, prevState) {
    this.checkLoadOrg(prevState);

    if (this.state.modal) {
      if (this.closeModal) {
        this.closeModal();
      }
      this.closeModal = mountModal(this.state.modal, this);
    }
  }

  closeModal = null;

  showModal(location, group) {
    const { router } = this.context;
    const modal = getModalToDisplayForQuery(router, group, location);

    if (modal) {
      this.setState({ modal });
      router.replace(location.pathname); // clear the query params
    }
  }

  getAffiliation() {
    const { staffClassOrganization } = this.state;
    return staffClassOrganization ?
      AffiliationStore.getAffiliation(staffClassOrganization.id) :
      null;
  }

  checkLoadOrg(prevState) {
    const prevGroup = prevState.group;
    const { group } = this.state;
    const prevGroupId = prevGroup && prevGroup.id;
    const groupId = group && group.id;
    if ((prevGroupId !== groupId) && groupId) {
      if (group.isStaffClass()) {
        OrganizationActions.getOrLoadOrganization(group.getStaffClassOrgId());
      }
    }
  }

  render() {
    const { group, userIsAdmin, accentColor } = this.state;
    const { children } = this.props;

    return (
      <GroupView
        accentColor={accentColor || DEFAULT_COLOR}
        group={group}
        userIsAdmin={userIsAdmin}>
        {children}
      </GroupView>
    );
  }
}

export default Container.create(GroupContainer, { withProps: true });



// WEBPACK FOOTER //
// ./src/groups/containers/GroupContainer.jsx